/* Isolated theme prototype. Shopify remains the authority for stock on cart/add. */
(function (root, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  } else {
    root.SCShippingCore = api;
  }
})(
  typeof globalThis !== 'undefined' ? globalThis : this,
  function () {
    'use strict';

    const PENDING_KEY = 'sc-split-test-pending-v1';

    const positiveInteger = (number) =>
      Number.isSafeInteger(Number(number)) &&
      Number(number) >= 0;

    function allocate(total, available, stockOnly = false) {
      if (
        !positiveInteger(total) ||
        !Number.isSafeInteger(Number(available))
      ) {
        throw new Error('Cantidad no válida.');
      }

      if (
        stockOnly &&
        Number(total) > Math.max(0, Number(available))
      ) {
        throw new Error(
          `La talla 3XL solo se vende con existencias. Quedan ${Math.max(
            0,
            Number(available)
          )} unidades; reduce la cantidad.`
        );
      }

      const express = Math.min(
        Number(total),
        Math.max(0, Number(available))
      );

      return {
        express,
        standard: Number(total) - express
      };
    }

    function buildPairs(config) {
      if (!config.enabled) return [];

      const explicit =
        config.options.length === 3 &&
        config.options[2] === 'Despacho';

      const legacy =
        config.options.length === 2 &&
        config.options[1] === 'Talla';

      if (!explicit && !legacy) {
        throw new Error(
          'El producto necesita Versión y Talla, o Versión, Talla y Despacho en ese orden.'
        );
      }

      const normalizeSize = (value) =>
        String(value)
          .replace(/[·•]/g, ' ')
          .replace(/express/ig, '')
          .replace(/-/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
          .toUpperCase()
          .replace(/^XL 1$/, 'XL')
          .replace(/^(\d+) XL$/, '$1XL');

      const pairs = new Map();

      for (const variant of config.variants) {
        const options = [
          variant.options[0],
          normalizeSize(variant.options[1])
        ];

        const key = JSON.stringify(options);

        const kind = explicit
          ? variant.options[2] === 'Express'
            ? 'express'
            : variant.options[2] === 'Estándar'
              ? 'standard'
              : null
          : /express/i.test(variant.options[1])
            ? 'express'
            : 'standard';

        if (!kind) {
          throw new Error(
            'Despacho solo admite Express y Estándar.'
          );
        }

        if (!pairs.has(key)) {
          pairs.set(key, {
            product: config,
            options,
            stockOnly: options[1] === '3XL'
          });
        }

        const pair = pairs.get(key);

        if (pair[kind]) {
          throw new Error(
            'Hay una combinación duplicada.'
          );
        }

        pair[kind] = variant;
      }

      for (const pair of pairs.values()) {
        if (
          !pair.express ||
          (!pair.standard && !pair.stockOnly)
        ) {
          throw new Error(
            'Falta una variante Express o Estándar para alguna talla.'
          );
        }

        if (pair.stockOnly && pair.standard) {
          throw new Error(
            'La talla 3XL debe tener solo la variante de stock Express, sin Estándar.'
          );
        }

        if (
          pair.standard &&
          pair.express.price !== pair.standard.price
        ) {
          throw new Error(
            'Express y Estándar deben tener el mismo precio.'
          );
        }

        if (
          pair.express.management !== 'shopify' ||
          pair.express.policy !== 'deny'
        ) {
          throw new Error(
            'Express debe controlar el inventario y detener la venta al agotarse.'
          );
        }

        if (
          pair.standard &&
          (
            pair.standard.management !== 'shopify' ||
            pair.standard.policy !== 'continue' ||
            pair.standard.inventory > 0
          )
        ) {
          throw new Error(
            'Estándar debe controlar inventario, permitir vender sin existencias y no tener stock positivo.'
          );
        }

        for (
          const variant of
          [pair.express, pair.standard].filter(Boolean)
        ) {
          if (
            variant.minimum !== 1 ||
            variant.increment !== 1 ||
            variant.maximum != null
          ) {
            throw new Error(
              'La prueba requiere cantidades por unidad, sin mínimos especiales ni máximos por variante.'
            );
          }
        }

        pair.id = String(pair.express.id);
      }

      return [...pairs.values()];
    }

    function personalPair(pair, requested) {
      const fixed = pair.product.dorsal?.included;
      const included = Boolean(requested?.included);

      if (
        (pair.stockOnly || !pair.standard) &&
        !included
      ) {
        throw new Error(
          'La talla 3XL solo se vende sin dorsal personalizado y con existencias.'
        );
      }

      const name = String(requested?.name || '')
        .trim()
        .toUpperCase();

      const number = String(requested?.number || '')
        .trim();

      if (
        !name ||
        name.length > 18 ||
        !/^[0-9]{1,3}$/.test(number)
      ) {
        throw new Error(
          'Escribe el nombre del dorsal (hasta 18 caracteres) y un número de hasta 3 cifras.'
        );
      }

      if (
        Boolean(fixed) !== included ||
        (
          fixed &&
          (
            name !== String(fixed.name)
              .trim()
              .toUpperCase() ||
            number !== String(fixed.number).trim()
          )
        )
      ) {
        throw new Error(
          'El dorsal incluido debe coincidir con la configuración del producto.'
        );
      }

      const fee = pair.product.dorsal?.fee;

      if (
        !included &&
        (
          !fee?.id ||
          fee.minimum !== 1 ||
          fee.increment !== 1 ||
          fee.maximum != null ||
          fee.requiresShipping !== false
        )
      ) {
        throw new Error(
          'No se pudo confirmar el servicio de dorsal. Debe ser un servicio sin envío y vendido por unidad.'
        );
      }

      const id =
        `${pair.id}|` +
        `${included ? 'incluido' : 'dorsal'}|` +
        `${encodeURIComponent(name)}|${number}`;

      return {
        ...pair,
        baseId: pair.id,
        id,
        personalization: {
          name,
          number,
          included
        },
        fee: included ? null : fee
      };
    }

    function patchPair(pair, requested) {
      if (!requested) return pair;

      return {
        ...pair,
        baseId: pair.baseId || pair.id,
        id: `${pair.id}|parches`,
        patches: true
      };
    }

    function personalProperties(
      pair,
      fee = false,
      kind = 'Estándar'
    ) {
      const personal = pair.personalization;

      return {
        _sc_split: pair.baseId,

        ...(fee
          ? {
              _sc_fee_for: pair.id,
              _sc_product_url: pair.product.url
            }
          : {
              _sc_personal: pair.id,
              Entrega:
                personal.included && kind === 'Express'
                  ? 'ENVÍO EXPRESS'
                  : 'ENVÍO ESTÁNDAR'
            }),

        Personalización:
          personal.included
            ? 'Dorsal incluido'
            : 'Con dorsal',

        Nombre: personal.name,
        Número: personal.number,

        ...(pair.patches
          ? fee
            ? {
                _sc_patches: pair.id
              }
            : {
                _sc_patches: pair.id,
                Parches: 'Con parches'
              }
          : {}),

        ...(fee
          ? {
              Para: pair.product.title
            }
          : {})
      };
    }

    function lineProperties(pair, kind) {
      if (pair.personalization) {
        return personalProperties(
          pair,
          false,
          kind
        );
      }

      return {
        _sc_split: pair.baseId || pair.id,

        ...(pair.patches
          ? {
              _sc_patches: pair.id,
              Parches: 'Con parches'
            }
          : {}),

        Entrega:
          kind === 'Express'
            ? 'ENVÍO EXPRESS'
            : 'ENVÍO ESTÁNDAR'
      };
    }

    function matchesPair(line, pair) {
      const personalMatch = pair.personalization
        ? line.properties?._sc_personal === pair.id
        : (
            !line.properties?._sc_personal &&
            !line.properties?._sc_fee_for
          );

      const patchMatch = pair.patches
        ? (
            line.properties?._sc_patches === pair.id &&
            line.properties?.Parches === 'Con parches'
          )
        : (
            !line.properties?._sc_patches &&
            !line.properties?.Parches
          );

      return personalMatch && patchMatch;
    }

    function groupCart(cart, configs) {
      const byVariant = new Map();

      for (const config of configs) {
        for (const pair of buildPairs(config)) {
          byVariant.set(
            String(pair.express.id),
            pair
          );

          if (pair.standard) {
            byVariant.set(
              String(pair.standard.id),
              pair
            );
          }
        }
      }

      const groups = new Map();
      const ordinary = [];
      const fees = [];

      for (const line of cart.items) {
        if (line.properties?._sc_fee_for) {
          fees.push(line);
          continue;
        }

        let pair = byVariant.get(
          String(line.variant_id)
        );

        if (!pair) {
          if (line.properties?._sc_split) {
            throw new Error(
              'Un artículo de prueba perdió su configuración. Revisa su plantilla y etiqueta.'
            );
          }

          ordinary.push(line);
          continue;
        }

        const personalized = Boolean(
          line.properties?._sc_personal
        );

        const patched =
          line.properties?.Parches === 'Con parches';

        const allowed = personalized
          ? [
              '_sc_split',
              '_sc_personal',
              '_sc_patches',
              'Entrega',
              'Personalización',
              'Nombre',
              'Número',
              'Parches'
            ]
          : [
              '_sc_split',
              '_sc_patches',
              'Entrega',
              'Parches'
            ];

        if (
          line.selling_plan_allocation ||
          Object.keys(line.properties || {}).some(
            (key) => !allowed.includes(key)
          )
        ) {
          throw new Error(
            'Esta prueba no admite suscripciones ni personalización. Revisa ese artículo antes de continuar.'
          );
        }

        if (personalized) {
          pair = personalPair(pair, {
            name: line.properties.Nombre,
            number: line.properties.Número,
            included:
              line.properties.Personalización ===
              'Dorsal incluido'
          });

          pair = patchPair(pair, patched);

          const validPersonalVariant =
            pair.personalization.included
              ? [pair.express, pair.standard]
                  .filter(Boolean)
                  .some(
                    (variant) =>
                      String(line.variant_id) ===
                      String(variant.id)
                  )
              : String(line.variant_id) ===
                String(pair.standard.id);

          if (
            pair.id !==
              line.properties._sc_personal ||
            !validPersonalVariant
          ) {
            throw new Error(
              pair.personalization.included
                ? 'El dorsal incluido no corresponde a una variante válida. Revisa ese artículo.'
                : 'El dorsal personalizado debe estar en su variante Estándar. Revisa ese artículo.'
            );
          }
        } else if (
          pair.product.dorsal?.included
        ) {
          throw new Error(
            'Este producto incluye un dorsal. Elimina la línea antigua y agrégalo de nuevo con su nombre y número.'
          );
        } else {
          pair = patchPair(pair, patched);
        }

        if (
          patched !== Boolean(
            line.properties?._sc_patches
          ) ||
          (
            patched &&
            pair.id !==
              line.properties._sc_patches
          )
        ) {
          throw new Error(
            'No se pudo confirmar la selección de parches. Elimina la línea y agrégala de nuevo.'
          );
        }

        if (!groups.has(pair.id)) {
          groups.set(pair.id, {
            pair,
            total: 0,
            express: 0,
            standard: 0,
            lines: [],
            fees: []
          });
        }

        const group = groups.get(pair.id);

        group.lines.push(line);
        group.total += line.quantity;

        group[
          String(line.variant_id) ===
          String(pair.express.id)
            ? 'express'
            : 'standard'
        ] += line.quantity;
      }

      for (const line of fees) {
        const properties = line.properties;

        const base = [...byVariant.values()].find(
          (pair) =>
            pair.id ===
              String(properties._sc_split) &&
            pair.product.url ===
              properties._sc_product_url
        );

        const allowedFeeProperties = [
          '_sc_split',
          '_sc_fee_for',
          '_sc_product_url',
          '_sc_patches',
          'Personalización',
          'Nombre',
          'Número',
          'Para'
        ];

        if (
          !base ||
          line.selling_plan_allocation ||
          Object.keys(properties).some(
            (key) =>
              !allowedFeeProperties.includes(key)
          )
        ) {
          throw new Error(
            'No se pudo vincular el cobro del dorsal con su jersey. Revisa el carrito.'
          );
        }

        let pair = personalPair(base, {
          name: properties.Nombre,
          number: properties.Número,
          included:
            properties.Personalización ===
            'Dorsal incluido'
        });

        pair = patchPair(
          pair,
          Boolean(properties._sc_patches)
        );

        if (
          !pair.fee ||
          pair.id !== properties._sc_fee_for ||
          String(line.variant_id) !==
            String(pair.fee.id)
        ) {
          throw new Error(
            'El cobro del dorsal no corresponde al producto. Revisa el carrito.'
          );
        }

        if (!groups.has(pair.id)) {
          groups.set(pair.id, {
            pair,
            total: 0,
            express: 0,
            standard: 0,
            lines: [],
            fees: []
          });
        }

        groups.get(pair.id).fees.push(line);
      }

      return {
        groups: [...groups.values()],
        ordinary
      };
    }

    class Engine {
      constructor({
        request,
        loadProduct,
        storage
      }) {
        this.request = request;
        this.loadProduct = loadProduct;
        this.storage = storage;
        this.queue = Promise.resolve();
      }

      exclusive(job) {
        const result = this.queue.then(job);

        this.queue = result.catch(() => {});

        return result;
      }

      pending() {
        const raw =
          this.storage?.getItem(PENDING_KEY);

        if (!raw) return null;

        try {
          return JSON.parse(raw);
        } catch (_) {
          throw new Error(
            'No se pudo leer la revisión pendiente del carrito.'
          );
        }
      }

      async snapshot(
        extraUrl,
        recoveryUrls = []
      ) {
        const cart =
          await this.request('cart.js');

        const paths = [
          ...new Set(
            cart.items
              .flatMap((line) => [
                line.url.split('?')[0],
                ...(line.properties?._sc_product_url
                  ? [
                      line.properties
                        ._sc_product_url
                    ]
                  : [])
              ])
              .concat(
                extraUrl
                  ? [extraUrl.split('?')[0]]
                  : [],
                recoveryUrls
              )
          )
        ];

        const configs = await Promise.all(
          paths.map((url) =>
            this.loadProduct(url)
          )
        );

        return {
          cart,
          configs,
          ...groupCart(cart, configs)
        };
      }

      async increase(
        variant,
        quantity,
        pair
      ) {
        if (quantity <= 0) return;

        const kind =
          String(variant.id) ===
          String(pair.express.id)
            ? 'Express'
            : 'Estándar';

        const current =
          await this.request('cart.js');

        const first = current.items.find(
          (line) =>
            String(line.variant_id) ===
              String(variant.id) &&
            matchesPair(line, pair)
        );

        const properties =
          first?.properties ||
          lineProperties(pair, kind);

        await this.request('cart/add.js', {
          items: [
            {
              id: variant.id,
              quantity,
              properties
            }
          ]
        });
      }

      async decrease(
        variant,
        target,
        pair
      ) {
        return this.decreaseMatching(
          target,
          (line) =>
            String(line.variant_id) ===
              String(variant.id) &&
            matchesPair(line, pair)
        );
      }

      async decreaseMatching(
        target,
        matches
      ) {
        let previous = Infinity;

        for (
          let step = 0;
          step < 100;
          step++
        ) {
          const current =
            await this.request('cart.js');

          const lines =
            current.items.filter(matches);

          const total = lines.reduce(
            (sum, line) =>
              sum + line.quantity,
            0
          );

          if (total <= target) return;

          if (total >= previous) {
            throw new Error(
              'El carrito cambió durante la revisión. Revisa el reparto antes de pagar.'
            );
          }

          previous = total;

          const line = lines.reduce(
            (largest, item) =>
              !largest ||
              item.quantity > largest.quantity
                ? item
                : largest,
            null
          );

          const quantity = Math.max(
            0,
            line.quantity -
              (total - target)
          );

          await this.request(
            'cart/change.js',
            {
              id: line.key,
              quantity
            }
          );
        }

        throw new Error(
          'No se pudo completar el ajuste del carrito. Pulsa Revisar de nuevo.'
        );
      }

      async reconcile(
        overrides,
        extraUrl,
        personalAddition
      ) {
        const pending = this.pending();

        const snapshot = await this.snapshot(
          extraUrl,
          pending?.urls || []
        );

        const desired =
          pending?.totals ||
          Object.fromEntries(
            snapshot.groups.map((group) => [
              group.pair.id,
              group.total
            ])
          );

        if (overrides) {
          Object.assign(desired, overrides);
        }

        const pairs =
          snapshot.configs.flatMap(buildPairs);

        const descriptions = {
          ...(pending?.personals || {})
        };

        for (const group of snapshot.groups) {
          if (
            group.pair.personalization ||
            group.pair.patches
          ) {
            descriptions[group.pair.id] = {
              baseId: group.pair.baseId,
              personalization:
                group.pair.personalization ||
                null,
              patches: Boolean(
                group.pair.patches
              )
            };
          }
        }

        if (personalAddition) {
          descriptions[personalAddition.id] = {
            baseId:
              personalAddition.baseId,
            personalization:
              personalAddition.personalization ||
              null,
            patches: Boolean(
              personalAddition.patches
            )
          };
        }

        for (
          const [id, description] of
          Object.entries(descriptions)
        ) {
          const base = pairs.find(
            (pair) =>
              pair.id === description.baseId
          );

          if (!base) {
            throw new Error(
              'No se pudo recuperar la configuración pendiente del producto.'
            );
          }

          const legacyPersonalization =
            description.name
              ? description
              : null;

          let pair =
            description.personalization ||
            legacyPersonalization
              ? personalPair(
                  base,
                  description.personalization ||
                    legacyPersonalization
                )
              : base;

          pair = patchPair(
            pair,
            Boolean(description.patches)
          );

          if (pair.id !== id) {
            throw new Error(
              'La configuración pendiente cambió. Revisa el carrito.'
            );
          }

          pairs.push(pair);
        }

        for (
          const [id, total] of
          Object.entries(desired)
        ) {
          if (
            !pairs.some(
              (pair) => pair.id === id
            ) ||
            !positiveInteger(total)
          ) {
            throw new Error(
              'El producto de una revisión pendiente ya no está disponible. No se ha completado el reparto.'
            );
          }

          const pair = pairs.find(
            (candidate) =>
              candidate.id === id
          );

          if (
            !pair.personalization ||
            pair.personalization.included
          ) {
            allocate(
              total,
              pair.express.inventory,
              pair.stockOnly
            );
          } else if (
            Number(total) > 0 &&
            pair.fee &&
            !pair.fee.available
          ) {
            throw new Error(
              'El servicio de dorsal no está disponible. Reduce o elimina la personalización antes de continuar.'
            );
          }
        }

        this.storage?.setItem(
          PENDING_KEY,
          JSON.stringify({
            totals: desired,
            personals: descriptions,
            urls: snapshot.configs
              .filter(
                (config) => config.enabled
              )
              .map(
                (config) => config.url
              )
          })
        );

        for (const pair of pairs) {
          if (
            !Object.hasOwn(
              desired,
              pair.id
            )
          ) {
            continue;
          }

          const target =
            pair.personalization &&
            !pair.personalization.included
              ? {
                  express: 0,
                  standard: Number(
                    desired[pair.id]
                  )
                }
              : allocate(
                  desired[pair.id],
                  pair.express.inventory,
                  pair.stockOnly
                );

          let cart =
            await this.request('cart.js');

          let counts =
            this.count(cart, pair);

          await this.increase(
            pair.express,
            Math.max(
              0,
              target.express -
                counts.express
            ),
            pair
          );

          cart =
            await this.request('cart.js');

          counts = this.count(cart, pair);

          if (pair.standard) {
            await this.increase(
              pair.standard,
              Math.max(
                0,
                target.standard -
                  counts.standard
              ),
              pair
            );
          }

          if (pair.fee) {
            cart =
              await this.request('cart.js');

            const feeCount =
              cart.items
                .filter(
                  (line) =>
                    line.properties
                      ?._sc_fee_for ===
                    pair.id
                )
                .reduce(
                  (sum, line) =>
                    sum + line.quantity,
                  0
                );

            if (
              feeCount < target.standard
            ) {
              await this.request(
                'cart/add.js',
                {
                  items: [
                    {
                      id: pair.fee.id,
                      quantity:
                        target.standard -
                        feeCount,
                      properties:
                        personalProperties(
                          pair,
                          true
                        )
                    }
                  ]
                }
              );
            }
          }

          await this.decrease(
            pair.express,
            target.express,
            pair
          );

          if (pair.standard) {
            await this.decrease(
              pair.standard,
              target.standard,
              pair
            );
          }

          if (pair.fee) {
            await this.decreaseMatching(
              target.standard,
              (line) =>
                String(line.variant_id) ===
                  String(pair.fee.id) &&
                line.properties
                  ?._sc_fee_for === pair.id
            );
          }

          cart =
            await this.request('cart.js');

          counts =
            this.count(cart, pair);

          const feeCount =
            cart.items
              .filter(
                (line) =>
                  line.properties
                    ?._sc_fee_for === pair.id
              )
              .reduce(
                (sum, line) =>
                  sum + line.quantity,
                0
              );

          if (
            counts.express !==
              target.express ||
            counts.standard !==
              target.standard ||
            (
              pair.fee &&
              feeCount !== target.standard
            )
          ) {
            throw new Error(
              'El carrito cambió durante la revisión. Revisa el reparto antes de pagar.'
            );
          }
        }

        await this.normalizeDeliveryLabels(
          snapshot.configs
        );

        const result =
          await this.snapshot();

        this.storage?.removeItem(
          PENDING_KEY
        );

        return result;
      }

      async normalizeDeliveryLabels(configs) {
        for (
          let step = 0;
          step < 1000;
          step++
        ) {
          const cart =
            await this.request('cart.js');

          const groups =
            groupCart(
              cart,
              configs
            ).groups;

          let change;

          for (const group of groups) {
            for (
              const line of group.lines
            ) {
              const label =
                String(line.variant_id) ===
                String(
                  group.pair.express.id
                )
                  ? 'ENVÍO EXPRESS'
                  : 'ENVÍO ESTÁNDAR';

              if (
                line.properties?.Entrega &&
                line.properties.Entrega !==
                  label
              ) {
                change = {
                  id: line.key,
                  quantity: line.quantity,
                  properties: {
                    ...line.properties,
                    Entrega: label
                  }
                };

                break;
              }
            }

            if (change) break;
          }

          if (!change) return;

          await this.request(
            'cart/change.js',
            change
          );
        }

        throw new Error(
          'No se pudo actualizar la información de entrega. Revisa de nuevo antes de pagar.'
        );
      }

      count(cart, pair) {
        return cart.items.reduce(
          (counts, line) => {
            if (!matchesPair(line, pair)) {
              return counts;
            }

            if (
              String(line.variant_id) ===
              String(pair.express.id)
            ) {
              counts.express +=
                line.quantity;
            }

            if (
              pair.standard &&
              String(line.variant_id) ===
                String(pair.standard.id)
            ) {
              counts.standard +=
                line.quantity;
            }

            return counts;
          },
          {
            express: 0,
            standard: 0
          }
        );
      }

      add(
        url,
        pairId,
        quantity,
        personalization,
        patches = false
      ) {
        return this.exclusive(async () => {
          if (
            !positiveInteger(quantity) ||
            Number(quantity) === 0
          ) {
            throw new Error(
              'Introduce una cantidad entera mayor que cero.'
            );
          }

          if (this.pending()) {
            throw new Error(
              'Abre el carrito y completa la revisión pendiente antes de agregar artículos.'
            );
          }

          const snapshot =
            await this.snapshot(url);

          const base =
            snapshot.configs
              .flatMap(buildPairs)
              .find(
                (pair) =>
                  pair.id === pairId
              );

          if (!base) {
            throw new Error(
              'La combinación seleccionada ya no está disponible.'
            );
          }

          const selected =
            personalization ||
            (
              base.product.dorsal?.included
                ? {
                    ...base.product.dorsal
                      .included,
                    included: true
                  }
                : null
            );

          let pair = selected
            ? personalPair(base, selected)
            : base;

          pair = patchPair(
            pair,
            Boolean(patches)
          );

          const current =
            snapshot.groups.find(
              (group) =>
                group.pair.id === pair.id
            )?.total || 0;

          return this.reconcile(
            {
              [pair.id]:
                current +
                Number(quantity)
            },
            url,
            selected || patches
              ? pair
              : null
          );
        });
      }

      setTotal(pairId, total) {
        return this.exclusive(() =>
          this.reconcile({
            [pairId]: Number(total)
          })
        );
      }

      review() {
        return this.exclusive(() =>
          this.reconcile()
        );
      }
    }

    return {
      allocate,
      buildPairs,
      groupCart,
      personalPair,
      patchPair,
      Engine
    };
  }
);
